import { isSameDay } from "date-fns";
import { getRandomAsin } from "src/api/demo";
import ImageIcon from "src/assets/icons/ImageIcon";
import VideoIcon from "src/assets/icons/VideoIcon";
import AudioIcon from "src/assets/icons/AudioIcon";
import AwarenessIcon from "src/assets/icons/AwarenessIcon";
import StreamingTvIcon from "src/assets/icons/StreamingTvIcon";
import ConversionsIcon from "src/assets/icons/ConversionsIcon";
import { customSymbols } from './currencySymbols'; // Import the custom symbols
import ConsiderationIcon from "src/assets/icons/ConsiderationIcon";
import { ASIN_REGEX } from 'src/views/pages/targetting/utils';

export const isAmazon = window.location.href.indexOf('/amazon') > -1;
export const isAmazonTargeting = window.location.href.indexOf('/amazon/targeting') > -1;
export const isAmazonRanking = window.location.href.indexOf('/amazon-ranking-dashboard') > -1;

export const getCustomCurrencySymbol = (currencyCode) => {
  return customSymbols[currencyCode] || customSymbols['INR']; // Default to INR if not found
};

export const isFlipkart = window.location.href.indexOf('/flipkart') > -1;

const selectedProfileId = isAmazon
  ? JSON.parse(sessionStorage.getItem('selectedProfileId'))
  : isFlipkart
  ? JSON.parse(sessionStorage.getItem('flipkartProfile'))
  : {};

export const isAllowAdminOrManager = () => {
  if (!isAmazon) return false;
  return selectedProfileId?.accessLevel?.toLowerCase() === 'admin' || selectedProfileId?.accessLevel?.toLowerCase() === 'manager';
};

export const yetToOnboarded = () => {
  let selectedProfileId = isAmazon
    ? JSON.parse(sessionStorage.getItem('selectedProfileId'))
    : isFlipkart
    ? JSON.parse(sessionStorage.getItem('flipkartProfile'))
    : {};
  return (selectedProfileId && Object.keys(selectedProfileId).length === 0) || !selectedProfileId;
};

export const isAllowEdit = selectedProfileId?.accessLevel?.toLowerCase() == 'admin';

export const BILLING_LIMIT_EXCEED_MESSAGE_FOR_USER = `As per the #planName# plan you have exceeded the user limit, so we request you to upgrade the access limits to continue this operation`;

export const BILLING_TRIAL_EXCEED = ` <div style="text-align: center; line-height: 23px"><span style="font-weight: 700">Your current plan does not support account onboarding.</span><br>
To continue, please upgrade your plan or contact Hector Support for assistance.</div>` // Redirect to `/account-settings`

export const BILLING_ALERT_WHEN_LIMIT_EXCEEDED = `<div style="text-align: center; line-height: 23px"><span style="font-weight: 700">Ad Account Limit Reached</span><br>Your current plan does not support the required number of ad accounts for onboarding. To proceed, please contact Hector Support at support@hectorai.live for assistance.</div>`

export const BILLING_LIMIT_EXCEED_MESSAGE_FOR_AD_ACCOUNT = `As per the #planName# plan you have exceeded the ad account limit, so we request you to upgrade the access limits to continue this operation`;

function formatNumberWithMaxDecimals(number) {
  const numberString = String(number);

  if (numberString && numberString.includes('.')) {
    const [integerPart, decimalPart] = numberString.split('.');
    const formattedDecimalPart = decimalPart.length > 3 ? decimalPart.slice(0, 3) : decimalPart;
    const formattedNumber = `${integerPart}.${formattedDecimalPart}`;
    return parseFloat(formattedNumber);
  }

  // If the number has no decimal point, return it as is
  return number;
}

export const getCalcValue = (cValue, typeOfBulkUpdate, amazonCampaignPlacementLevel= false) => {
  const currentValue = Number(cValue);

  if (typeOfBulkUpdate.type === 'set_inc_bid_amount') {
    const calc = Number(currentValue) + Number(typeOfBulkUpdate.value);
    return formatNumberWithMaxDecimals(calc);
  } else if (typeOfBulkUpdate.type === 'set_dec_bid_amount') {
    const calc = Number(currentValue) - Number(typeOfBulkUpdate.value);
    const noFloor = typeOfBulkUpdate?.floor === '' || typeOfBulkUpdate?.floor === undefined;
    if (amazonCampaignPlacementLevel && noFloor && calc < 0) {
      return formatNumberWithMaxDecimals(0);
    }
    return formatNumberWithMaxDecimals(calc);
  } else if (typeOfBulkUpdate.type === 'set_bid_or_budget') {
    return typeOfBulkUpdate.value;
  } else if (typeOfBulkUpdate.type === 'set_inc_bid_or_budget_perc') {
    const decimalPercentage = typeOfBulkUpdate.value / 100;
    const increaseAmount = Number(currentValue) * decimalPercentage;
    return formatNumberWithMaxDecimals(Number(currentValue) + Number(increaseAmount));
  } else if (typeOfBulkUpdate.type === 'set_dec_bid_or_budget_perc') {
    const decimalPercentage = typeOfBulkUpdate.value / 100;
    const decreaseAmount = Number(currentValue) * decimalPercentage;
    return formatNumberWithMaxDecimals(Number(currentValue) - Number(decreaseAmount));
  }
};

export const getCalcFkValue = (cValue, typeOfBulkUpdate, checkStatus = false) => {
  const currentValue = Number(cValue);
  let { cap = null, floor = null } = typeOfBulkUpdate;

  // Normalize cap and floor: treat null, undefined, empty string as not set
  cap = (cap !== null && cap !== undefined && cap !== '') ? Number(cap) : null;
  floor = (floor !== null && floor !== undefined && floor !== '') ? Number(floor) : null;

  let result;

  if (typeOfBulkUpdate.type === 'set_inc_bid_amount') {
    const calc = Number(currentValue) + Number(typeOfBulkUpdate.value);
    result = formatNumberWithMaxDecimals(calc);
  } else if (typeOfBulkUpdate.type === 'set_dec_bid_amount') {
    const calc = Number(currentValue) - Number(typeOfBulkUpdate.value);
    result = formatNumberWithMaxDecimals(calc);
  } else if (typeOfBulkUpdate.type === 'set_bid_or_budget') {
    result = Number(typeOfBulkUpdate.value);
  } else if (typeOfBulkUpdate.type === 'set_inc_bid_or_budget_perc') {
    const decimalPercentage = typeOfBulkUpdate.value / 100;
    const increaseAmount = Number(currentValue) * decimalPercentage;
    result = formatNumberWithMaxDecimals(Number(currentValue) + Number(increaseAmount));
  } else if (typeOfBulkUpdate.type === 'set_dec_bid_or_budget_perc') {
    const decimalPercentage = typeOfBulkUpdate.value / 100;
    const decreaseAmount = Number(currentValue) * decimalPercentage;
    result = formatNumberWithMaxDecimals(Number(currentValue) - Number(decreaseAmount));
  } else {
    // If type is not recognized, just return the current value
    result = currentValue;
  }

  // Apply cap and floor if they are set and are valid numbers
  if (cap !== null && !isNaN(cap) && result > cap && checkStatus) {
    result = parseFloat(Number(cap).toFixed(2));
  }
  if (floor !== null && !isNaN(floor) && result < floor && checkStatus) {
    result = parseFloat(Number(floor).toFixed(2));
  }

  return result;
};

export const getProceedStatus = (cValue, typeOfBulkUpdate, amazonCampaignPlacementLevel= false) => {
  if ((!cValue && !amazonCampaignPlacementLevel) || !typeOfBulkUpdate || !typeOfBulkUpdate.type) {
    return false;
  }

  const currentValue = Number(cValue);
  if (isNaN(currentValue)) {
    return false;
  }

  let updateValue;
  let incPercentage;
  let decPercentage;

  try {
    switch (typeOfBulkUpdate.type) {
      case 'set_inc_bid_amount':
        updateValue = formatNumberWithMaxDecimals(currentValue + Number(typeOfBulkUpdate.value));
        break;
      case 'set_dec_bid_amount':
        updateValue = formatNumberWithMaxDecimals(currentValue - Number(typeOfBulkUpdate.value));
        break;
      case 'set_bid_or_budget':
        updateValue = Number(typeOfBulkUpdate.value);
        break;
      case 'set_inc_bid_or_budget_perc':
        incPercentage = Number(typeOfBulkUpdate.value) / 100;
        updateValue = formatNumberWithMaxDecimals(currentValue + (currentValue * incPercentage));
        break;
      case 'set_dec_bid_or_budget_perc':
        decPercentage = Number(typeOfBulkUpdate.value) / 100;
        updateValue = formatNumberWithMaxDecimals(currentValue - (currentValue * decPercentage));
        break;
      default:
        return false;
    }
  } catch (error) {
    console.error('Error calculating update value:', error);
    return false;
  }

  if (isNaN(updateValue)) {
    return false;
  }

  const label = (typeOfBulkUpdate.label || '').toLowerCase();
  const updateType = label.includes('set') ? 'set' : 
                    label.includes('inc') ? 'inc' :
                    label.includes('dec') ? 'dec' : null;

  if (!updateType) {
    return false;
  }

  const floor = typeOfBulkUpdate?.floor === undefined || typeOfBulkUpdate?.floor === '' ? null : Number(typeOfBulkUpdate.floor);
  const cap = typeOfBulkUpdate?.cap === undefined || typeOfBulkUpdate?.cap === '' ? null : Number(typeOfBulkUpdate.cap);

  // For 'set' type
  if (updateType === 'set') {
    // if (floor === null && cap === null) {
    //   return true;
    // }
    // if (floor !== null && cap !== null) {
    //   return updateValue >= floor && updateValue <= cap;
    // }
    // if (floor !== null) {
    //   return updateValue >= floor;
    // }
    // if (cap !== null) {
    //   return updateValue <= cap;
    // }
    if (amazonCampaignPlacementLevel && updateValue > 900) {
      return false;
    }
    return true;
  }

  // For 'inc' type
  if (updateType === 'inc') {
    if (amazonCampaignPlacementLevel && updateValue > 900) {
      return false;
    }
    if (cap === null) {
      return true;
    }
    return updateValue <= cap;
  }

  // For 'dec' type
  if (updateType === 'dec') {
    if (amazonCampaignPlacementLevel) {
      return updateValue >= floor;
    }
    if (floor === null) {
      return true;
    }
    return updateValue >= floor;
  }

  return false;
};

export const getProductLink = (country, productId) => {
  let amazonUrl = 'https://www.amazon.com'; // Default to the US Amazon URL

  let newProductId = productId
  
  const authUser = JSON.parse(localStorage.getItem('User'))
  if(process.env.REACT_APP_DEMO_EMAIL && process.env.REACT_APP_DEMO_EMAIL.indexOf(authUser?.email) > -1) {
    newProductId = getRandomAsin()
  }

  // Check the country code and update the URL accordingly
  if (country === 'CA') {
    amazonUrl = 'https://www.amazon.ca';
  } else if (country === 'UK') {
    amazonUrl = 'https://www.amazon.co.uk';
  } else if (country === 'IN') {
    amazonUrl = 'https://www.amazon.in';
  } else if (country === 'US') {
    amazonUrl = 'https://www.amazon.com';
  } else if (country === 'BR') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'MX') {
    amazonUrl = 'https://www.amazon.com.mx';
  } else if (country === 'ES') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'FR') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'NL') {
    amazonUrl = 'https://www.amazon.nl';
  } else if (country === 'DE') {
    amazonUrl = 'https://www.amazon.de';
  } else if (country === 'IT') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'SE') {
    amazonUrl = 'https://www.amazon.se';
  } else if (country === 'PL') {
    amazonUrl = 'https://www.amazon.pl';
  } else if (country === 'EG') {
    amazonUrl = 'https://www.amazon.eg';
  } else if (country === 'TR') {
    amazonUrl = 'https://www.amazon.com.tr';
  } else if (country === 'SA') {
    amazonUrl = 'https://www.amazon.sa';
  } else if (country === 'AE') {
    amazonUrl = 'https://www.amazon.ae';
  } else if (country === 'SG') {
    amazonUrl = 'https://www.amazon.sg';
  } else if (country === 'AU') {
    amazonUrl = 'https://www.amazon.com.au';
  } else if (country === 'JP') {
    amazonUrl = 'https://www.amazon.co.jp';
  }

  // Append the product ID to the URL
  amazonUrl += '/dp/' + String(newProductId).toUpperCase();
  return amazonUrl;
}

const MYNTRA_GLOBAL_COUNTRY_CODES = ['US', 'CA', 'UK', 'AE', 'SA', 'SG', 'MY', 'AU', 'ZA', 'LK', 'NP'];

export const getMyntraProductLink = (countryCode, productId) => {
  if (!productId) return '';
  const baseUrl = countryCode && MYNTRA_GLOBAL_COUNTRY_CODES.indexOf(String(countryCode).toUpperCase()) > -1
    ? 'https://www.myntraglobal.com'
    : 'https://www.myntra.com';
  const id = String(productId).trim();
  return id ? `${baseUrl}/${id}` : baseUrl;
};

export const getSearchKeywordLink = (country, productId) => {
  let amazonUrl = 'https://www.amazon.com'; // Default to the US Amazon URL

  // Check the country code and update the URL accordingly
  if (country === 'CA') {
    amazonUrl = 'https://www.amazon.ca';
  } else if (country === 'UK') {
    amazonUrl = 'https://www.amazon.co.uk';
  } else if (country === 'IN') {
    amazonUrl = 'https://www.amazon.in';
  } else if (country === 'US') {
    amazonUrl = 'https://www.amazon.com';
  } else if (country === 'BR') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'MX') {
    amazonUrl = 'https://www.amazon.com.mx';
  } else if (country === 'ES') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'FR') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'NL') {
    amazonUrl = 'https://www.amazon.nl';
  } else if (country === 'DE') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'IT') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'SE') {
    amazonUrl = 'https://www.amazon.se';
  } else if (country === 'PL') {
    amazonUrl = 'https://www.amazon.pl';
  } else if (country === 'EG') {
    amazonUrl = 'https://www.amazon.eg';
  } else if (country === 'TR') {
    amazonUrl = 'https://www.amazon.com.tr';
  } else if (country === 'SA') {
    amazonUrl = 'https://www.amazon.sa';
  } else if (country === 'AE') {
    amazonUrl = 'https://www.amazon.ae';
  } else if (country === 'SG') {
    amazonUrl = 'https://www.amazon.sg';
  } else if (country === 'AU') {
    amazonUrl = 'https://www.amazon.com.au';
  } else if (country === 'JP') {
    amazonUrl = 'https://www.amazon.co.jp';
  }

  // Append the product ID to the URL
  amazonUrl += '/s?k=' + productId;
  return amazonUrl;
}

export const isAsinOrExpandedAsin = (displayValue) => {
  const isAsin = String(displayValue).indexOf('asin="') > -1 || String(displayValue).indexOf('asin-expanded="') > -1
  return isAsin
}

export const allEmptyValues = (obj)  => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] !== '') {
      return false; // If any non-empty value is found, return false
    }
  }
  return true; // If no non-empty values are found, return true
}

export const filterNonEmptyValues = (obj) => {
  return Object.keys(obj).filter(key => obj[key] !== '').reduce((acc, key) => {
    acc[key] = obj[key];
    return acc;
  }, {});
}

export const validateEmail = (email) => {
  var re = /\S+@\S+\.\S+/;
  return re.test(email);
}

export const TIMESPEND_FILTER = [
  {
    group_key: 'all',
    group_name: 'All'
  },
  {
    group_key: 'clients',
    group_name: 'Clients'
  },
  {
    group_key: 'internal',
    group_name: 'Internal Team'
  }
]

export const createProductUrl = (productId) => {
  const selectedProfileId = JSON.parse(sessionStorage.getItem('selectedProfileId'))
  const country = selectedProfileId?.countryCode;

  let amazonUrl = 'https://www.amazon.com'; // Default to the US Amazon URL

  // Check the country code and update the URL accordingly
  if (country === 'CA') {
    amazonUrl = 'https://www.amazon.ca';
  } else if (country === 'UK') {
    amazonUrl = 'https://www.amazon.com.uk';
  } else if (country === 'IN') {
    amazonUrl = 'https://www.amazon.in';
  } else if (country === 'US') {
    amazonUrl = 'https://www.amazon.com';
  } else if (country === 'BR') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'MX') {
    amazonUrl = 'https://www.amazon.com.mx';
  } else if (country === 'ES') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'FR') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'NL') {
    amazonUrl = 'https://www.amazon.nl';
  } else if (country === 'DE') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'IT') {
    amazonUrl = 'https://www.amazon.com.br';
  } else if (country === 'SE') {
    amazonUrl = 'https://www.amazon.se';
  } else if (country === 'PL') {
    amazonUrl = 'https://www.amazon.pl';
  } else if (country === 'EG') {
    amazonUrl = 'https://www.amazon.eg';
  } else if (country === 'TR') {
    amazonUrl = 'https://www.amazon.com.tr';
  } else if (country === 'SA') {
    amazonUrl = 'https://www.amazon.com.sa';
  } else if (country === 'AE') {
    amazonUrl = 'https://www.amazon.ae';
  } else if (country === 'SG') {
    amazonUrl = 'https://www.amazon.sg';
  } else if (country === 'AU') {
    amazonUrl = 'https://www.amazon.com.au';
  } else if (country === 'JP') {
    amazonUrl = 'https://www.amazon.co.jp';
  }

  // Append the product ID to the URL
  amazonUrl += '/dp/' + productId;
  return amazonUrl;
}
// 'SearchTerm',  - Accidentally not being used much or irrevant keywords/asins are negatived, targeted
export const BULK_SELECT_ALL_TABLES = ['CampaignName', 'Targeting', 'AdGroupName', 'AdvertisedASINs', 'SearchTerm', 'AudienceName',  'productId', 'Campaigns', 'AdGroup', 'FsnId']

export const BULK_SELECT_MARKETING_STREAM = ['CampaignName', 'Targeting']

export const BULK_SELECT_LABELS = {
  'SearchTerm' : ' search terms', 
  'CampaignName': ' campaigns', 
  'Targeting': ' targets', 
  'AdGroupName': ' ad groups',
  'AdvertisedASINs': ' products',
  'productId' : ' products',
  'productId' : ' products',
  'Campaigns': ' campaigns',
  'AdGroup': ' ad groups',
  'FsnId': ' products'
}
const currencyCode = selectedProfileId?.currencyCode;

export const MEDIA_TYPE_LIST = [
  {
    id: 1,
    icon: <ImageIcon />,
    title: "Display",
    value: "DISPLAY",
    subTitle: "Image ads that serve across Amazon and third-party",
  },
  {
    id: 2,
    icon: <VideoIcon />,
    title: "Online video",
    value: "VIDEO_OLV",
    subTitle: "Video ads that serve on online video inventory",
  },
  {
    id: 3,
    icon: <StreamingTvIcon />,
    title: "Streaming TV",
    value: "VIDEO_STV",
    subTitle: "Video ads that serve on streaming TV inventory",
  },
  {
    id: 4,
    icon: <AudioIcon />,
    title: "Audio",
    value: "AUDIO",
    subTitle: "Audio ads that serve on streaming audio and podcast inventory",
  }
]

export const GOALS_AND_OPTIMIZATION = [
  {
    id: 1,
    icon: <AwarenessIcon />,
    title: "Awareness - TOF",
    key: "AWARENESS",
    subTitle: "Expose your brand or product to more potential customers",
    kpi: [
      {
        id: 1,
        title: "Reach",
        key: "REACH",
        subTitle: "The number of unique users exposed from campaign start",
        subComponent: <></>
      },
      {
        id: 2,
        title: "Frequency",
        key: "FREQUENCY",
        subTitle: "The number of times an ad is shown to one user",
        subComponent: <></>
      }
    ]
  },
  {
    id: 2,
    icon: <ConsiderationIcon />,
    title: "Consideration - MOF",
    key: "CONSIDERATION",
    subTitle: "Drive potential customers to your store or website",
    kpi: [
      {
        id: 1,
        title: "Click through rate (CTR)",
        shortName: "CTR",
        key: "CLICK_THROUGH_RATE",
        subTitle: "The number of click-throughs relative to the number of impressions",
        kpiTargetPrefix:"%",
        subComponent: <></>
      },
      {
        id: 2,
        title: "Cost per click (CPC)",
        shortName: "CPC",
        key: "COST_PER_CLICK",
        subTitle: "The average cost for each click on an ad",
        kpiTargetPrefix: getCustomCurrencySymbol(currencyCode),
        subComponent: <></>
      },
      {
        id: 3,
        title: "Cost per video completion (CPVC)",
        shortName: "CPVC",
        key: "COST_PER_VIDEO_COMPLETION",
        subTitle: "The average cost to acquire a video complete conversion",
        kpiTargetPrefix: getCustomCurrencySymbol(currencyCode),
        subComponent: <></>
      },
      {
        id: 4,
        title: "Video completion rate (VCR)",
        shortName: "VCR",
        key: "VIDEO_COMPLETION_RATE",
        subTitle: "The number of video completions relative to the number of video starts",
        kpiTargetPrefix:"%",
        subComponent: <></>
      },
      {
        id: 5,
        title: "Cost per detail page view (CPDPV)",
        shortName: "CPDPV",
        key: "COST_PER_DETAIL_PAGE_VIEW",
        subTitle: "The average cost to acquire a detail page view for a promoted product on Amazon",
        kpiTargetPrefix: getCustomCurrencySymbol(currencyCode),
        subComponent: <></>
      },
      {
        id: 6,
        title: "Detail page view rate (DPVR)",
        shortName: "DPVR",
        key: "DETAIL_PAGE_VIEW_RATE",
        subTitle: "The number of detail page views for a promoted product on Amazon relative to the number of ad impressions",
        kpiTargetPrefix:"%",
        subComponent: <></>
      },
    ]
  },
  {
    id: 3,
    icon: <ConversionsIcon />,
    title: "Conversions - BOF",
    key: "CONVERSIONS",
    subTitle: "Drive purchases, leads, app installs, and other customer conversions",
    kpi: [
      {
        id: 1,
        title: "Return on ad spend (ROAS)",
        shortName: "ROAS",
        key: "RETURN_ON_AD_SPEND",
        subTitle: "The average return on ad spend for promoted products sold on Amazon",
        kpiTargetPrefix:"",
        subComponent: <></>
      },
      {
        id: 2,
        title: "Total ROAS (T-ROAS)",
        shortName: "T-ROAS",
        key: "TOTAL_RETURN_ON_AD_SPEND",
        subTitle: "The average return on ad spend for all of a brand's promoted products sold on Amazon",
        kpiTargetPrefix:"",
        subComponent: <></>
      },
      {
        id: 3,
        title: "Cost per action (CPA)",
        shortName: "CPA",
        key: "COST_PER_ACTION",
        subTitle: "The average cost per completed action",
        kpiTargetPrefix: getCustomCurrencySymbol(currencyCode),
        subComponent: <></>
      },
      {
        id: 4,
        title: "Combined ROAS (C-ROAS)",
        shortName: "C-ROAS",
        key: "COMBINED_RETURN_ON_AD_SPEND",
        subTitle: "The average return on ad spend for all of a brand's promoted products sold on and off Amazon",
        kpiTargetPrefix:"",
        subComponent: <></>
      },
      {
        id: 5,
        title: "Cost per sign up (CPSU)",
        shortName: "CPSU",
        key: "COST_PER_SIGN_UP",
        subTitle: "The average cost to acquire a new free trial or paid app subscription for an Amazon Prime Video channel or streaming app",
        kpiTargetPrefix: getCustomCurrencySymbol(currencyCode),
        subComponent: <></>
      },
      {
        id: 6,
        title: "Cost per first app open (CPFAO)",
        shortName: "CPFAO",
        key: "COST_PER_FIRST_APP_OPEN",
        subTitle: "The average cost to acquire a first-time app open",
        kpiTargetPrefix: getCustomCurrencySymbol(currencyCode),
        subComponent: <></>
      },
    ]
  }
]

export const  PRIMARY_INVENTORY_TYPES = [
 {    id: 1,
    icon:  <ImageIcon />,
    title: "Display",
    key: "DISPLAY",
    subTitle: "Image ads that serve across Amazon and third-party",
  },
  {    id: 2,
    icon:  <VideoIcon />,
    title: "Online Video",
    key: "VIDEO_OLV",
    subTitle: "Video ads that serve on online video inventory",
  },
  // {    id: 3,
  //   icon:  <StreamingTvIcon />,
  //   title: "Streaming TV",
  //   key: "VIDEO_STV",
  //   subTitle: "Video ads that serve on streaming TV inventory",
  // }
]
export const getKeywordCount = (selectedRows) => 
  selectedRows.filter(item => item.SearchTerm && !item.SearchTerm.includes('b0') && !ASIN_REGEX.test(item.SearchTerm)).length;

export const getAsinCount = (selectedRows) => 
  selectedRows.filter(item => item.SearchTerm && (item.SearchTerm.includes('b0') || ASIN_REGEX.test(item.SearchTerm))).length;

export const todayIsActive = ({ startDate, endDate } = {}) => {
  if (!startDate || !endDate) return false;
  const today = new Date();
  return startDate && endDate && isSameDay(startDate, endDate) && isSameDay(startDate, today);
};

export const getCountryFlagUrl = (country) => {
  const countryCode = String(country).toLowerCase()
  return countryCode === 'uk' ? 'https://flagpedia.net/data/flags/w1160/gb.webp' : `https://flagcdn.com/${countryCode}.svg`
}


let countryListCache = null;
let fetchInProgress = null;

const findCountryDetails = (codes) => {
  return codes.map(code => {
    const country = countryListCache.find(c => c.country_code === code);
    return {
      country_code: code,
      country_name: country?.country_name || '',
    };
  });
};

export const getCountryNameByCode = async (input) => {
  // Ensure codes is always an array for uniform processing
  const codes = Array.isArray(input) ? input : [input];

  // If cache exists, return directly
  if (countryListCache) {
    return findCountryDetails(codes);
  }

  // If fetch is in progress, wait for it
  if (fetchInProgress) {
    await fetchInProgress;
    return findCountryDetails(codes);
  }

  // Start fetching and store the promise
  fetchInProgress = fetch('https://restcountries.com/v2/all?fields=name,alpha2Code')
    .then(res => res.json())
    .then(result => {
      countryListCache = result.map(country => ({
        country_name: country.name,
        country_code: country.alpha2Code
      }));
    });

  await fetchInProgress;
  fetchInProgress = null;

  return findCountryDetails(codes);
};

export const sortObjectKeys = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  return Object.keys(obj)
    .sort()
    .reduce((result, key) => {
      result[key] = obj[key];
      return result;
    }, {});
};